// engine
import {
  ArBitmapText,
  ArContainer,
  ArGraphics,
  ArSprite,
  Counter,
  currencyService,
  delay,
  gameDispatcher,
  gsap,
  ORIENTATION,
  screenDispatcher,
  soundService,
  SPECIAL_SPIN_MODE,
  SPIN_MODE,
  userDispatcher
} from '@aceroll/core';

// library
import { ArBigWin } from '@aceroll/core/library';

const BIG_WIN_CONTENT_SKIN = {
  ENGLISH: 'English',
  SPANISH: 'Spanish',
};

const bigWinConfig = {
	background: {
		baseSize: {
			[ORIENTATION.LANDSCAPE]: {
				width: 1568,
				height: 1080
			},
			[ORIENTATION.PORTRAIT]: {
				width: 1168,
				height: 2340
			}
		}
	},
	content: {
		baseSize: {
			[ORIENTATION.LANDSCAPE]: {
				width: 1840,
				height: 1080
			},
			[ORIENTATION.PORTRAIT]: {
				width: 1400,
				height: 2340
			}
		},
		spine: {
			src: 'spine-big_win_popup',
			animations: {
				show: 'appear'
			},
			skins: {
				default: 'English'
			}
		}
	},
	tiers: [
		{
			name: 'big-win',
			multiplier: 50,
			idle: 'big_idle',
			duration: 1000,
			sounds: {},
			background: {
				main: 'img-background_big_win',
				extend: 'img-background_big_win_extend'
			},
			skip: new Map([
				[1, 'big_to_mega'],
				[2, 'big_to_supermega'],
				[3, 'big_to_epic']
			])
		},
		{
			name: 'mega-win',
			multiplier: 100,
			idle: 'mega_idle',
			transition: 'transition',
			duration: 1200,
			sounds: {
				transition: 'db_super_win'
			},
			background: {
				main: 'img-background_super_win',
				extend: 'img-background_super_win_extend'
			},
			skip: new Map([
				[2, 'mega_to_supermega'],
				[3, 'mega_to_epic']
			])
		},
		{
			name: 'supermega-win',
			multiplier: 250,
			idle: 'supermega_idle',
			transition: 'transition_1',
			duration: 1400,
			sounds: {
				transition: 'db_mega_win'
			},
			background: {
				main: 'img-background_mega_win',
				extend: 'img-background_mega_win_extend'
			},
			skip: new Map([
				[3, 'supermega_to_epic']
			])
		},
		{
			name: 'epic-win',
			multiplier: Infinity,
			idle: 'epic_idle_1',
			transition: 'transition_2',
			duration: 1600,
			sounds: {
				transition: 'db_supermega_win'
			},
			background: {
				main: 'img-background_supermega_win',
				extend: 'img-background_supermega_win_extend'
			}
		}
	],
	sounds: {
		show: 'db_big_win',
		hide: null,
		count: 'db_win_counter'
	}
};

export class BigWinPopup extends ArBigWin {
	#tl1 = gsap.timeline();
	#tl2 = gsap.timeline();

	bgLs = null;
	bgPt = null;

	backgrounds = [];
	currentBackground = null;

	bgLsGraphics = null;
	bgPtGraphics = null;

	counter = null;

	text = null;

	bet = null;
	win = null;
	multiplier = null;

	#currentSpineSkin = null;

	#currentValue = 0;

	#leftText = null;
	#centerText = null;
	#rightText = null;

	#currencySymbol = '';
	#currencyDelimiter = '';

	#winLength = 0;

	#currentTierIndex = -1;
	#destinationTierIndex = -1;

	#interrupted = false;
	#animationResolver = null;
	#backgroundResolver = null;
	#textContainer = null;

	constructor() {
		super(bigWinConfig);

		this.updateValue = this.updateValue.bind(this);

		this.setup();

		window.show = this.show.bind(this);
	}

	set interrupted(value) {
		this.#interrupted = value;

		if (!value) {
			return;
		}

		if (this.counter.isPlaying) {
			this.counter.stop(false);
		}
	}

	set skin(value) {
		this.#currentSpineSkin = value;
		this.contentSpine.setSkin(value);
	}

	get skin() {
		return this.#currentSpineSkin;
	}

	setup() {
		super.setup();

    this.#setupContentSpineSkin();
		this.#setupBackground();
		this.#setupText();
		this.#setupCounter();

		this.addListener('pointerup', this.#interrupt, this);
	}

	#setupBackground() {
		const { background, tiers } = this.config;

		this.bgLsGraphics = new ArGraphics(background.label + '-graphics');
		this.bgLsGraphics.rect(0, 0, background.baseSize[ORIENTATION.LANDSCAPE].width, background.baseSize[ORIENTATION.LANDSCAPE].height);
		this.bgLsGraphics.fill('#000000');
		this.bgLsGraphics.alpha = 0.6;

		this.bgPtGraphics = new ArGraphics(background.label + '-graphics');
		this.bgPtGraphics.fill('#000000');
		this.bgPtGraphics.rect(0, 0, background.baseSize[ORIENTATION.PORTRAIT].width, background.baseSize[ORIENTATION.PORTRAIT].height);
		this.bgPtGraphics.fill();
		this.bgPtGraphics.alpha = 0.6;

		this.background.addChild(this.bgLsGraphics, this.bgPtGraphics);

		// backgrounds

		this.bgLs = new ArContainer(background.label + '-ls');
		this.bgPt = new ArContainer(background.label + '-pt');

		for (const tier of tiers) {
			const lsContainer = new ArContainer(background.label + '-ls-' + tier.name);
			const ptContainer = new ArContainer(background.label + '-pt-' + tier.name);

			const mainLsSprite = ArSprite.from(tier.background.main);
			mainLsSprite.x = (mainLsSprite.width - 1568) / 2 * -1;
			mainLsSprite.y = -200 - (mainLsSprite.height - 747);

			const mainPtSprite = ArSprite.from(tier.background.main);
			mainPtSprite.x = (mainPtSprite.width - 1168) / 2 * -1;
			mainPtSprite.y = 450 - (mainPtSprite.height - 747);

			const extendSprite = ArSprite.from(tier.background.extend);
			extendSprite.anchor.set(0.5, 1);
			extendSprite.scale.x = 1.2;
			extendSprite.x = 584;
			extendSprite.y = 450 - (mainPtSprite.height - 747);

			lsContainer.addChild(mainLsSprite);
			ptContainer.addChild(mainPtSprite, extendSprite);

			this.bgLs.addChild(lsContainer);
			this.bgPt.addChild(ptContainer);

			lsContainer.alpha = 0;
			lsContainer.angle = -40;
			lsContainer.x = -400;

			ptContainer.alpha = 0;
			ptContainer.angle = -40;
			ptContainer.x = -400;

			const bgObject = {
				[ORIENTATION.LANDSCAPE]: lsContainer,
				[ORIENTATION.PORTRAIT]: ptContainer
			};

			this.backgrounds.push(bgObject);
		}

		this.background.addChild(this.bgLs, this.bgPt);
	}

	#setupText() {
		const { label } = this.config;

		const currency = currencyService.toCurrency(105320241);
		this.#currencySymbol = currency[0];
		this.#currencyDelimiter = currency[currency.length - 3];

		this.text = new ArContainer(label + '-text');

		const textStyle = {
			fontSize: 56,
			align: 'center'
		};

		this.#leftText = ArBitmapText.from('bitmap-win-line', '0', label + '-text-left', textStyle);
		this.#centerText = ArBitmapText.from('bitmap-win-line', this.#currencyDelimiter, label + '-text-center', textStyle);
		this.#rightText = ArBitmapText.from('bitmap-win-line', '00', label + '-text-right', textStyle);

		this.#leftText.anchor.x = 1;
		this.#centerText.anchor.x = 0.5;
		this.#rightText.anchor.x = 0;

		this.#leftText.x = this.#centerText.width / 2 * -1;
		this.#rightText.x = this.#centerText.width / 2;

		// this.text.addChild(this.#leftText, this.#centerText, this.#rightText);
		this.text.addChild(this.#centerText);

		// this.text.pivot.y = 100;
		this.text.alpha = 0;

		this.#adjustWinString();

		this.#textContainer = new ArContainer('counter-text');
		this.#textContainer.addChild(this.text);
		this.contentSpine.addToSlot(this.#textContainer, 'text_holder');
	}

	#setupCounter() {
		this.counter = new Counter({
			duration: 5000,
			callback: this.updateValue
		});
	}

	async #showSequence() {
		const { sounds } = this.config;
		const { show } = this.config.content.spine.animations;

		const startPromises = Promise.all([
			this.#createAnimationPromise(show),
			this.#showBackground(0)
		]);

		const firstTier = this.config.tiers[0];

		if (this.#destinationTierIndex === 0) {
			this.counter.playFromTo(this.#currentValue, this.win, firstTier.duration);
		} else {
			this.counter.playFromTo(this.#currentValue, this.bet * firstTier.multiplier, firstTier.duration);
		}

		this.text.fade(1, 50);

		if (sounds.show) {
			soundService.play(sounds.show);
		}

		soundService.play(this.config.sounds.count);
		delay(firstTier.duration).then(() => soundService.stop(this.config.sounds.count));

		return startPromises;
	}

	async show(bet, win) {
		this.bet = bet;
		this.win = win;
		this.multiplier = this.win / this.bet;

		this.#adjustWinString();

		this.eventMode = 'dynamic';
		this.#setDestination();

		this.visible = true;

		await this.#showSequence();

		for (let i = 0; i <= this.#destinationTierIndex; i++) {
			if (!this.#interrupted) {
				await this.#transition();
			}
		}

		if (this.#interrupted) {
			this.#interruptTransition();
		}

		this.eventMode = 'auto';

		const mode = userDispatcher.mode;

		switch (mode) {
			case SPIN_MODE.DEFAULT:
				await delay(2000);
				break;
			case SPIN_MODE.TURBO:
				await delay(1600);
				break;
			case SPIN_MODE.SUPER_TURBO:
				await delay(1400);
				break;
			case SPECIAL_SPIN_MODE:
				await delay(1200);
				break;
		}


		await this.hide();
	}

	async hide() {
		this.background.fade(0, 250);
		await this.content.fade(0, 250);

		this.visible = false;

		this.text.alpha = 0;

		if (this.currentBackground) {
			this.currentBackground[ORIENTATION.LANDSCAPE].alpha = 0;
			this.currentBackground[ORIENTATION.LANDSCAPE].angle = -40;
			this.currentBackground[ORIENTATION.LANDSCAPE].x = -400;

			this.currentBackground[ORIENTATION.PORTRAIT].alpha = 0;
			this.currentBackground[ORIENTATION.PORTRAIT].angle = -40;
			this.currentBackground[ORIENTATION.PORTRAIT].x = -400;

			this.currentBackground = null;
		}

		this.content.alpha = 1;
		this.background.alpha = 1;

		this.contentSpine.stop(true);
		this.contentSpine.reset(false);

		this.#leftText.text = '0';
		this.#rightText.text = '00';
		this.counter.value = 0;

		this.#currentTierIndex = -1;
		this.#destinationTierIndex = -1;

		this.#currentValue = 0;

		this.#winLength = 0;

		this.#interrupted = false;
	}

	updateValue(value) {
		this.#currentValue = value;

		let currency = currencyService.toMoney(Math.floor(value));


		const delimiterIndex = currency.indexOf(this.#currencyDelimiter);
		const split = currency.split(this.#currencyDelimiter);
		const leftText = split[0];
		const rightText = split[1];
		// this.#leftText.text = leftText;
		// this.#rightText.text = rightText;
		this.#centerText.text = currency;

		// if (leftText.length > this.#winLength) {
		// 	this.#winLength = leftText.length;
		// 	this.#adjustWinString();
		// }
	}

	#adjustWinString() {
		this.text.y = -50;

	}

	#setDestination() {
		const { tiers } = this.config;

		for (let i = 0; i < tiers.length; i++) {
			const tier = tiers[i];

			if (this.multiplier <= tier.multiplier) {
				this.#destinationTierIndex = i;
				break;
			}
		}

		if (this.#destinationTierIndex === -1) {
			this.#destinationTierIndex = tiers.length - 1;
		}
	}

	async #transition() {
		const { tiers } = this.config;
		const tierIndex = this.#currentTierIndex + 1;
		const tier = tiers[tierIndex];

		this.contentSpine.stop(true);

		const { sounds } = tier;

		if (sounds.transition) {
			soundService.play(sounds.transition);
		}

		if (tier.transition) {
			await Promise.all([
				this.#createAnimationPromise(tier.transition),
				this.#showBackground(tierIndex)
			]);
		}

		this.contentSpine.stop(true);
		this.contentSpine.setImmediately(tier.idle, true);

		if (this.#interrupted) {
			this.#currentTierIndex += 1;
			return;
		}

		if (tierIndex === 0) {
			await this.counter.playPromise;
		} else if (tierIndex === this.#destinationTierIndex) {
			soundService.play(this.config.sounds.count);
			delay(tier.duration).then(() => soundService.stop(this.config.sounds.count));

			await this.counter.playFromTo(this.#currentValue, this.win, tier.duration);
		} else {
			soundService.play(this.config.sounds.count);
			delay(tier.duration).then(() => soundService.stop(this.config.sounds.count));

			await this.counter.playFromTo(this.#currentValue, this.bet * tier.multiplier, tier.duration);
		}

		this.#currentTierIndex += 1;
	}

	async #showBackground(tierIndex, immediately = false) {
		const { orientation } = screenDispatcher.state;
		const oppositeOrientation = orientation === ORIENTATION.LANDSCAPE ? ORIENTATION.PORTRAIT : ORIENTATION.LANDSCAPE;

		const backgroundObject = this.backgrounds[tierIndex];

		if (immediately) {
			if (this.currentBackground) {
				this.currentBackground[orientation].alpha = 0;
				this.currentBackground[orientation].angle = -40;
				this.currentBackground[orientation].x = -400;

				this.currentBackground[oppositeOrientation].alpha = 0;
				this.currentBackground[oppositeOrientation].angle = -40;
				this.currentBackground[oppositeOrientation].x = -400;
			}

			backgroundObject[orientation].alpha = 1;
			backgroundObject[orientation].angle = 0;
			backgroundObject[orientation].x = 0;
			backgroundObject[oppositeOrientation].alpha = 1;
			backgroundObject[oppositeOrientation].angle = 0;
			backgroundObject[oppositeOrientation].x = 0;

			this.currentBackground = backgroundObject;

			return;
		}

		this.#backgroundResolver = () => {
			this.#tl1.progress(1);
			this.#tl2.progress(1);
		};

		if (this.currentBackground) {
			backgroundObject[oppositeOrientation].alpha = 1;
			backgroundObject[oppositeOrientation].angle = 0;
			backgroundObject[oppositeOrientation].x = 0;

			this.currentBackground[oppositeOrientation].alpha = 0;
			this.currentBackground[oppositeOrientation].angle = -40;
			this.currentBackground[oppositeOrientation].x - 400;

			await Promise.all([
				this.#tl1.to(backgroundObject[orientation], {
					ease: 'power1.inOut',
					duration: 0.25,
					delay: 0.4,
					alpha: 1,
					angle: 0,
					x: 0
				}),
				this.#tl2.to(this.currentBackground[orientation], {
					ease: 'power1.in',
					duration: 0.1,
					alpha: 0,
					delay: 0.4
				})
			]);

			this.currentBackground[orientation].angle = -40;
			this.currentBackground[orientation].x = -400;
		} else {
			await this.#tl1.to(backgroundObject[orientation], {
				ease: 'power1.inOut',
				duration: 0.3,
				delay: 0.2,
				alpha: 1,
				angle: 0,
				x: 0
			});

			backgroundObject[oppositeOrientation].alpha = 1;
			backgroundObject[oppositeOrientation].angle = 0;
			backgroundObject[oppositeOrientation].x = 0;
		}

		this.#tl1.clear();
		this.#tl2.clear();

		this.currentBackground = backgroundObject;
	}

	#createAnimationPromise(animationName) {
		return new Promise(res => {
			this.contentSpine.setImmediately(animationName).then(res);
			this.#animationResolver = res;
		});
	}

	async #interruptTransition() {
		const { tiers } = this.config;
		const tier = tiers[this.#destinationTierIndex];

		this.contentSpine.stop(true);
		this.contentSpine.reset();

		if (
			tiers[this.#currentTierIndex] &&
			tiers[this.#currentTierIndex].skip &&
			tiers[this.#currentTierIndex].skip.has(this.#destinationTierIndex)
		) {
			const skipAnimation = tiers[this.#currentTierIndex].skip.get(this.#destinationTierIndex);
			await this.contentSpine.setImmediately(skipAnimation);
			this.#showBackground(this.#destinationTierIndex, true);
		} else {
			this.#showBackground(this.#destinationTierIndex, true);
		}

		this.contentSpine.setImmediately(tier.idle, true);

		this.counter.playFromTo(this.#currentValue, this.win, 200);
		soundService.play(this.config.sounds.count);
		delay(200).then(() => soundService.stop(this.config.sounds.count));
	}

	// events
	async #interrupt() {
		this.eventMode = 'auto';
		this.interrupted = true;

		this.counter.stop(false);
		soundService.stop(this.config.sounds.count);

		if (this.#animationResolver) {
			this.#animationResolver();
		}

		if (this.#backgroundResolver) {
			this.#backgroundResolver();
		}
	}

	// view
	onLandscape() {
		this.contentSpine.x = 932;
		this.contentSpine.y = 500;

		this.bgLsGraphics.visible = true;
		this.bgPtGraphics.visible = false;

		this.bgLs.visible = true;
		this.bgPt.visible = false;
	}

	onPortrait() {
		this.contentSpine.x = 710;
		this.contentSpine.y = 1170;

		this.bgPtGraphics.visible = true;
		this.bgLsGraphics.visible = false;

		this.bgPt.visible = true;
		this.bgLs.visible = false;
	}

	destroy(...args) {
		this.removeListener('pointerup', this.#interrupt, this);

		return super.destroy(args);
	}

  #setupContentSpineSkin() {
    const { localeCode } = gameDispatcher.state;
    const skinToUse = localeCode === 'es-es' ? BIG_WIN_CONTENT_SKIN.SPANISH : BIG_WIN_CONTENT_SKIN.ENGLISH;
    this.contentSpine.setSkin(skinToUse);
  }
}
